/**
 * UserProvider.tsx  ver5
 * ─────────────────────────────────────────────────────────────────────────────
 * src/app/providers/UserProvider.tsx
 *
 * ver5 - July 2026:
 *   - Fetch and expose per-account media limit overrides
 *     (override_max_photos / videos / docs) as `mediaOverrides`.
 *     Null means "use tier default"; a number overrides it.
 *     NOTE: requires migration v45 on the target database - deploying
 *     this against a DB without those columns breaks the profile fetch.
 *
 * ver4 - May 2026:
 *   - FIX: auth-lock deadlock / app-wide query hang.
 *     The onAuthStateChange listener called fetchProfile() directly, which
 *     calls supabase.auth.getSession() and a profiles query. Running a
 *     Supabase call INSIDE the onAuthStateChange callback re-enters the auth
 *     lock while it is still held, deadlocking the client: no request is sent,
 *     and every subsequent query hangs until a full page refresh. The fetch is
 *     now deferred with setTimeout(…, 0) so the callback returns and releases
 *     the lock before the query runs. (Supabase-documented pattern.)
 *
 * ver3 - April 2026:
 *   - FIX: "fields only load after refresh" race condition.
 *     Root cause: Supabase client's session restore from localStorage is async.
 *     On cold mount the query could fire before the auth token was attached,
 *     causing RLS to return nothing silently. Refresh worked because by then
 *     the session was already cached in memory.
 *     Fix:
 *       1. Await supabase.auth.getSession() before firing the profile query.
 *          This forces the client to finish its session init first.
 *       2. Subscribe to onAuthStateChange - if the session arrives AFTER our
 *          first attempt, refetch automatically (Supabase-recommended pattern).
 *       3. Log errors to console instead of swallowing them silently.
 *       4. Ensure isLoading always becomes false (was stuck on empty userId).
 *
 * ver2 - April 2026:
 *   - Added avatar_url and byline to profile fetch
 *   - Added first_name, family_name for future use
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../shared/services/supabase';
import { loadThemePrefs, getDefaultPrefs, type ThemePrefs } from '../../shared/services/themeService';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UserProfile {
  id:                  string;
  display_name:        string;
  first_name:          string | null;
  family_name:         string | null;
  date_of_birth:       string | null;
  current_country:     string | null;
  current_city:        string | null;
  avatar_url:          string | null;
  byline:              string | null;
  subscription_tier:   'free' | 'discovery' | 'family' | 'lifetime';
  has_capsule_echo:    boolean;
  has_legacy_wave:     boolean;
  onboarding_complete: boolean;
  override_max_photos: number | null;
  override_max_videos: number | null;
  override_max_docs:   number | null;
}

interface UserContextValue {
  profile:         UserProfile | null;
  themePrefs:      ThemePrefs;
  isLoading:       boolean;
  isDiscovery:     boolean;   // true if discovery, family, or lifetime
  hasCapsuleEcho:  boolean;
  hasLegacyWave:   boolean;
  // Per-account media limit overrides (null = use tier default).
  mediaOverrides: {
    photos: number | null;
    videos: number | null;
    docs:   number | null;
  };
  refreshProfile:  () => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const UserContext = createContext<UserContextValue | null>(null);

// ─── Constants ────────────────────────────────────────────────────────────────

const PROFILE_COLUMNS =
  'id, display_name, first_name, family_name, date_of_birth, current_country, ' +
  'current_city, avatar_url, byline, subscription_tier, has_capsule_echo, ' +
  'has_legacy_wave, onboarding_complete, ' +
  'override_max_photos, override_max_videos, override_max_docs';

// ─── Provider ─────────────────────────────────────────────────────────────────

interface UserProviderProps {
  userId:   string;
  children: React.ReactNode;
}

export function UserProvider({ userId, children }: UserProviderProps) {
  const [profile,    setProfile]    = useState<UserProfile | null>(null);
  const [themePrefs, setThemePrefs] = useState<ThemePrefs>(getDefaultPrefs());
  const [isLoading,  setIsLoading]  = useState(true);

  // Tracks which userId we've already successfully loaded a profile for.
  // Prevents duplicate fetches when onAuthStateChange fires for the same user.
  const loadedUserRef = useRef<string | null>(null);

  // ── fetchProfile ───────────────────────────────────────────────
  const fetchProfile = useCallback(async function () {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: wait for Supabase's session to be fully restored.
      // Without this, the query below may fire BEFORE the auth token is
      // attached to outgoing requests, which causes RLS to silently
      // return nothing on a cold page load. This is the bug that made
      // the user have to press refresh for fields to appear.
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        // No session yet. Don't fire the query - the onAuthStateChange
        // listener below will call us again once the session arrives.
        console.warn('[UserProvider] Skipping fetch - no session yet. Waiting for auth state change.');
        setIsLoading(false);
        return;
      }

      // Step 2: with the session confirmed, fetch profile + theme in parallel.
      const [profileResult, resolvedTheme] = await Promise.all([
        supabase
          .from('profiles')
          .select(PROFILE_COLUMNS)
          .eq('id', userId)
          .single(),
        loadThemePrefs(userId),
      ]);

      if (profileResult.error) {
        // Log loud. If RLS or a column change ever breaks this query
        // again, we want to see it in the console immediately rather
        // than staring at a page of empty fields.
        console.error('[UserProvider] Profile fetch failed:', profileResult.error);
      } else if (profileResult.data) {
        setProfile(profileResult.data as unknown as UserProfile);
        loadedUserRef.current = userId;
      }

      setThemePrefs(resolvedTheme);
    } catch (err) {
      console.error('[UserProvider] fetchProfile threw:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // ── Initial load on mount / userId change ──────────────────────
  useEffect(function () {
    setIsLoading(true);
    fetchProfile();
  }, [fetchProfile]);

  // ── Auth state change listener ─────────────────────────────────
  // If the session becomes available AFTER our initial fetch attempt
  // (e.g. Supabase finished restoring from localStorage moments later,
  // or a token refresh completed), re-run fetchProfile once.
  // We guard against duplicate fetches using loadedUserRef.
  //
  // IMPORTANT: the fetch is deferred with setTimeout(…, 0). The callback
  // runs while Supabase holds its internal auth lock; calling fetchProfile
  // (which calls getSession() and a query) directly inside it re-enters
  // that lock and deadlocks the whole client - no request goes out and
  // every later query hangs until a hard refresh. Deferring lets the
  // callback return and release the lock first.
  useEffect(function () {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      function (_event, session) {
        if (
          session?.user?.id === userId &&
          loadedUserRef.current !== userId
        ) {
          setTimeout(function () {
            fetchProfile();
          }, 0);
        }
      }
    );

    return function () {
      subscription.unsubscribe();
    };
  }, [userId, fetchProfile]);

  // ── Derived values ─────────────────────────────────────────────
  const tier = profile?.subscription_tier ?? 'free';
  const isDiscovery = tier === 'discovery' || tier === 'family' || tier === 'lifetime';

  const value: UserContextValue = {
    profile,
    themePrefs,
    isLoading,
    isDiscovery,
    hasCapsuleEcho:  profile?.has_capsule_echo ?? false,
    hasLegacyWave:   profile?.has_legacy_wave ?? false,
    mediaOverrides: {
      photos: profile?.override_max_photos ?? null,
      videos: profile?.override_max_videos ?? null,
      docs:   profile?.override_max_docs ?? null,
    },
    refreshProfile:  fetchProfile,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used inside <UserProvider>');
  }
  return ctx;
}
